import { ThematicPackage } from '@features/campaigns/shared/types/thematicPackage.ts';
import { AccessRight } from '@shared/api/commonApiInterfaces';
import { CampaignId } from '@shared/types/campaign.ts';
import { Status, ValueOf, DataStatuses } from '@shared/types/common';
import { UserCountryCode } from '@shared/types/countryCodes.ts';

export const CampaignStatuses = {
  DRAFT: 'DRAFT',
  BUDGET_APPROVAL: 'BUDGET_APPROVAL',
  FAILED: 'FAILED',
  IN_APPROVAL: 'IN_APPROVAL',
  APPROVED: 'APPROVED',
  DELIVERING: 'DELIVERING',
  COMPLETED: 'COMPLETED',
  PAUSED: 'PAUSED',
} as const;

export const CampaignTypes = {
  INTERNAL: 'INTERNAL',
  SELF_SERVICE: 'SELF_SERVICE',
} as const;

// TODO: use country codes
export const CampaignCountry = {
  ITALY: 'IT',
  SPAIN: 'ES',
  GERMANY: 'DE',
} as const;

export enum AdProductType {
  INTERNAL = 'INTERNAL',
  SELF_SERVICE = 'SELF_SERVICE',
}

export const CampaignTypesPrefixes = {
  INTERNAL: 'M',
  SELF_SERVICE: 'S',
};

export type CampaignCountryType = ValueOf<typeof CampaignCountry>;
export type CampaignType = ValueOf<typeof CampaignTypes>;

export const allCampaignStatuses = Object.values(CampaignStatuses);
export const allCampaignTypes = Object.values(CampaignTypes);
export const allCampaignCountries = Object.values(CampaignCountry);

export type CampaignStatus = ValueOf<typeof CampaignStatuses>;

export type DraftCampaignStatus = typeof CampaignStatuses.DRAFT;

export const CampaignFormats = {
  DISPLAY: 'DISPLAY',
  VIDEO: 'VIDEO',
} as const;

export type CampaignFormat = ValueOf<typeof CampaignFormats>;

export const allCampaignFormats = Object.values(CampaignFormats);

export const CampaignMediaTypes = {
  CROSS: 'CROSS',
  CTV: 'CTV',
  THEMATIC: 'THEMATIC',
} as const;

export type CampaignMediaType = ValueOf<typeof CampaignMediaTypes>;

export const internalCampaignMediaTypes = [CampaignMediaTypes.CTV, CampaignMediaTypes.CROSS] as const;

export const externalCampaignMediaTypes = [CampaignMediaTypes.CTV, CampaignMediaTypes.THEMATIC] as const;

export type ExternalCampaignMediaType = (typeof externalCampaignMediaTypes)[number];

export const OperationType = {
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE',
} as const;

export type Operation = ValueOf<typeof OperationType>;
export type IncludeOperation = ValueOf<typeof OperationType.INCLUDE>;

export const CAMPAIGN_BUILDER_STEPS = {
  STEP_01: 'STEP_01',
  STEP_02: 'STEP_02',
  STEP_03: 'STEP_03',
  STEP_04: 'STEP_04',
  STEP_05: 'STEP_05',
} as const;

export type CampaignBuilderStep = ValueOf<typeof CAMPAIGN_BUILDER_STEPS>;

export const CreativeServices = {
  FULL: 'FULL',
  LIGHT: 'LIGHT',
} as const;

export const DisplayAdProduct = {
  SELF_SERVICE: 'SELF_SERVICE',
  INTERNAL: 'INTERNAL',
} as const;

export type CreativeService = ValueOf<typeof CreativeServices>;

export const CREATIVE_FILE_TYPES = {
  LSHAPE: 'LSHAPE',
  LANDING: 'LANDING',
  SWITCH_IN_LEFT: 'SWITCH_IN_LEFT',
  SWITCH_IN_BOTTOM: 'SWITCH_IN_BOTTOM',
} as const;

export const CreativeTypes = {
  CAMPAIGN_LANDING: 'CAMPAIGN_LANDING',
  CAMPAIGN_L_SHAPE: 'CAMPAIGN_L_SHAPE',
  CAMPAIGN_LEFT_L_SHAPE: 'CAMPAIGN_LEFT_L_SHAPE',
  CAMPAIGN_BOTTOM_L_SHAPE: 'CAMPAIGN_BOTTOM_L_SHAPE',
} as const;

export type PossibleCreativeFileType = keyof typeof CREATIVE_FILE_TYPES;

export const HighImpactCreativeTypes = {
  INTRO_MOBILE: 'INTRO_MOBILE',
  MASTHEAD: 'MASTHEAD',
  MASTHEAD_ADAPTIVE: 'MASTHEAD_ADAPTIVE',
  SKIN: 'SKIN',
} as const;

export type HighImpactCreativeType = ValueOf<typeof HighImpactCreativeTypes>;

export const HighImpactCreativeStatuses = {
  APPROVED: 'APPROVED',
  PROCESSING: 'PROCESSING',
  PENDING_MANUAL_REVIEW: 'PENDING_MANUAL_REVIEW',
  REJECTED: 'REJECTED',
} as const;

type HighImpactCreativeStatus = ValueOf<typeof HighImpactCreativeStatuses>;

export interface Advertiser {
  id: string;
  name: string;
  isPreliminaryBlocked: boolean;
  status: Status;
  code: string;
}

interface Brand {
  id: string;
  name: string;
  status: Status;
  code: string;
}

export interface Agency {
  id: string;
  name: string;
  status: Status;
  code: string;
}

export interface BillingContact {
  id: string;
  name: string;
  status: Status;
  code: string;
}

export interface GenericTargetCollectionItem {
  id: string;
  name: string;
  status: Status;
  isAiBased?: boolean;
}

export interface Provinces {
  operationType: Operation | null;
  items: Omit<GenericTargetCollectionItem, 'status'>[];
}

export interface AgeGender {
  operationType: Operation | null;
  items: GenericTargetCollectionItem[];
}

export interface InterestSegments {
  operationType: Operation | null;
  items: GenericTargetCollectionItem[];
}

export interface CampaignPricing {
  impressions: number;
  bonusImpressions: number;
  bonusPrice: boolean;
  availableImpressions: number;
  estimatedReach: number;
  estimatedFrequency: number;
  estimatedReachRange: [number, number];
  estimatedFrequencyRange: [number, number];
  cpm: number;
  budget: number;
  targetSize: number;
}

export const InvalidSections = {
  ADVERTISER: 'ADVERTISER',
  BILLING_CONTACT: 'BILLING_CONTACT',
  END_DATE: 'END_DATE',
  TARGETING: 'TARGETING',
  INTEREST_SEGMENTS_TARGETING: 'INTEREST_SEGMENTS_TARGETING',
  AGE_GENDER_TARGETING: 'AGE_GENDER_TARGETING',
  CONTENT_PACKAGE_TARGETING: 'CONTENT_PACKAGE_TARGETING',
  AD_PRODUCTS: 'AD_PRODUCTS',
  BUDGET: 'BUDGET',
} as const;

export type InvalidSection = ValueOf<typeof InvalidSections>;

export interface InactiveTargeting {
  id: string;
  name: string;
}

export interface InvalidSectionDetailsData {
  inactiveInterestSegments?: InactiveTargeting[];
  inactiveAgeGender?: InactiveTargeting[];
  inactiveContentPackages?: InactiveTargeting[];
}

export interface InvalidSectionsItem {
  section: InvalidSection;
  details: string;
  detailsData?: InvalidSectionDetailsData;
}

export interface AdProductDisplay {
  data: {
    creativeServices: ValueOf<typeof CreativeServices>;
    numberOfCreatives: number;
  };
  creatives?: {
    type: ValueOf<typeof CreativeTypes>;
    containsPoliticalContent: boolean;
  }[];
  lShapeImageUrl: string | null;
  landingImageUrl: string | null;
  bottomLShapeImageUrl: string | null;
  leftLShapeImageUrl: string | null;
}

export interface HighImpactCreative {
  type: HighImpactCreativeType;
  url: string;
  status: HighImpactCreativeStatus;
  containsPoliticalContent: boolean;
}

export interface AdProductHighImpact {
  landingPageUrl: string;
  creatives: HighImpactCreative[];
}

export type AdProductVideoCreativeStatus = ValueOf<typeof DataStatuses>;

interface AdProductVideoCreative {
  fileStorageId: string;
  originalFilename: string;
  status: AdProductVideoCreativeStatus;
  containsPoliticalContent: boolean;
  url: string;
}

export type AdProductVideo = AdProductVideoSelfService | AdProductVideoInternal;

export interface AdProductVideoSelfService {
  data: {
    trackingUrl: string;
  };
  creative: AdProductVideoCreative | null;
  type: AdProductType;
}

export interface AdProductVideoInternal {
  data: {
    linearAddressable: boolean;
    vod: boolean;
  };
  creative: null;
  type: AdProductType;
}

export interface CampaignDetails {
  id: CampaignId;
  sequentialId: number;
  billingSequentialId: number | null;
  name: string;
  creatorName: string;
  createdBy: string;
  isNameAutoGenerated: boolean;
  startDate: string;
  endDate: string;
  isAgencyCommissionGross: boolean;
  advertiser: Advertiser;
  agency: Agency;
  brand: Brand;
  budget: number;
  status: CampaignStatus;
  provinces: Provinces | null;
  cities: Provinces | null;
  contentPackage: GenericTargetCollectionItem | null;
  thematicPackage?: ThematicPackage | null;
  ageGender: AgeGender | null;
  interestSegments: InterestSegments | null;
  additionalNote: string;
  adProductDisplay: AdProductDisplay | null;
  adProductVideo: AdProductVideoInternal | AdProductVideoSelfService | null;
  adProductHighImpact: AdProductHighImpact | null;
  pricing: CampaignPricing;
  invalidSections: InvalidSectionsItem[];
  inventory: boolean;
  accessRights: AccessRight[];
  type: CampaignType;
  purchaseOrder: string | null;
  billingContact: BillingContact | null;
  authorizedBudget?: number | null;
  realBudget?: number | null;
  mediaType: CampaignMediaType;
  country: UserCountryCode;
}

export type AvailableAdProducts = {
  [k in CampaignFormat]: boolean;
};

export type PossibleStepNumber = keyof typeof CAMPAIGN_BUILDER_STEPS;
